import type { Rng } from '../core/rng';
import { MOVES } from './data';
import type { AiProfile, ActiveMove, FighterState, MoveId, OpponentDef, Projectile } from './types';

export interface AiIntent {
  walk: -1 | 0 | 1;
  block: boolean;
  move: MoveId | null;
}

const IDLE: AiIntent = { walk: 0, block: false, move: null };

/** Each night in a row the crowd gets a little rowdier. */
export function scaledProfile(p: AiProfile, level: number): AiProfile {
  const l = Math.max(0, level - 1);
  const faster = Math.pow(0.9, l);
  return {
    ...p,
    aggression: Math.min(0.92, p.aggression + 0.06 * l),
    reaction: Math.max(0.16, p.reaction * faster),
    blockChance: Math.min(0.6, p.blockChance + 0.05 * l),
    projectileBlock: Math.min(0.6, p.projectileBlock + 0.05 * l),
    guard: Math.min(0.8, p.guard + 0.05 * l),
    reversal: Math.min(0.8, p.reversal + 0.05 * l),
    think: [Math.max(0.18, p.think[0] * faster), Math.max(0.3, p.think[1] * faster)],
  };
}

/**
 * A readable, beatable opponent: it walks to its favourite range, attacks
 * with telegraphed moves, and sometimes blocks what it sees coming.
 */
export class Ai {
  private thinkIn: number;
  private walkFor = 0;
  private walkDir: -1 | 0 | 1 = 0;
  private blockUntil = 0;
  private reactAt: number | null = null;
  private reactWithBlock = false;
  private seenAttack: ActiveMove | null = null;
  private readonly seenProjectiles = new Set<number>();
  private wasStunned = false;
  private punished: ActiveMove | null = null;
  private inRange = false;

  constructor(
    private readonly def: OpponentDef,
    private readonly profile: AiProfile,
    private readonly rng: Rng,
  ) {
    this.thinkIn = rng.range(0.3, 0.7);
  }

  update(
    dt: number,
    t: number,
    self: FighterState,
    foe: FighterState,
    projectiles: readonly Projectile[],
  ): AiIntent {
    const distance = Math.abs(foe.x - self.x);

    // Notice attacks early (their "tell"), then react after a human-ish delay.
    const threat = foe.action;
    if (
      threat &&
      threat !== this.seenAttack &&
      threat.def.reach > 0 &&
      distance <= threat.def.reach + 18
    ) {
      this.seenAttack = threat;
      this.reactAt = t + this.profile.reaction;
      this.reactWithBlock = this.rng.chance(this.profile.blockChance);
    }
    if (this.reactAt !== null && t >= this.reactAt) {
      if (this.reactWithBlock) this.blockUntil = Math.max(this.blockUntil, t + 0.45);
      this.reactAt = null;
    }
    for (const p of projectiles) {
      if (p.owner === self.side || this.seenProjectiles.has(p.id)) continue;
      const incoming = Math.sign(self.x - p.x) === Math.sign(p.vx);
      const gap = Math.abs(self.x - p.x);
      if (incoming && gap < 80) {
        this.seenProjectiles.add(p.id);
        if (this.rng.chance(this.profile.projectileBlock)) {
          this.blockUntil = Math.max(this.blockUntil, t + gap / Math.abs(p.vx) + 0.15);
        }
      }
    }

    const stunned = self.stun > 0 || self.parried > 0;
    const justRecovered = this.wasStunned && !stunned;
    this.wasStunned = stunned;
    if (self.action || stunned) return IDLE;

    // Just ate a hit: either turtle up or swing right back.
    if (justRecovered && distance < 40) {
      if (this.rng.chance(this.profile.guard)) {
        this.blockUntil = Math.max(this.blockUntil, t + this.rng.range(0.45, 0.8));
      } else if (this.rng.chance(this.profile.reversal)) {
        const quick = this.quickMove(distance, self);
        if (quick) return { walk: 0, block: false, move: quick };
      }
    }
    // Punish a whiffed or blocked string while it's recovering.
    const foeMove = foe.action;
    if (
      foeMove &&
      foeMove !== this.punished &&
      foeMove.t > foeMove.def.startup + foeMove.def.active &&
      distance < 34 &&
      foeMove.def.reach > 0
    ) {
      this.punished = foeMove;
      if (this.rng.chance(this.profile.reversal)) {
        const quick = this.quickMove(distance, self);
        if (quick) {
          this.blockUntil = 0;
          return { walk: 0, block: false, move: quick };
        }
      }
    }
    if (t < this.blockUntil) return { walk: 0, block: true, move: null };

    if (this.walkFor > 0) {
      this.walkFor -= dt;
      return { walk: this.walkDir, block: false, move: null };
    }

    // Neutral: the moment you step into their range, they decide what to do about it.
    const reach = this.meleeReach();
    if (!this.inRange && distance <= reach) {
      this.inRange = true;
      this.thinkIn = Math.min(this.thinkIn, this.profile.reaction * 0.5);
    } else if (distance > reach + 8) {
      this.inRange = false;
    }

    this.thinkIn -= dt;
    if (this.thinkIn > 0) return IDLE;
    // Up close, customers make decisions faster.
    const close = distance < 40 ? 0.55 : 1;
    this.thinkIn = this.rng.range(this.profile.think[0], this.profile.think[1]) * close;

    const options = this.profile.moves.filter((m) => {
      if (distance < m.minRange || distance > m.maxRange) return false;
      if ((self.cooldowns[m.move] ?? 0) > 0) return false;
      if (m.move === 'throw' && !this.def.projectile) return false;
      if (m.move === 'throw' && projectiles.some((p) => p.owner === self.side)) return false;
      return true;
    });
    const wantsTaunt =
      options.some((o) => o.move === 'taunt') && this.rng.chance(this.profile.tauntChance);
    if (wantsTaunt) return { walk: 0, block: false, move: 'taunt' };
    const attacks = options.filter((o) => o.move !== 'taunt');
    if (attacks.length > 0 && this.rng.chance(this.profile.aggression)) {
      const pick = this.rng.weighted(attacks.map((o) => [o.move, o.weight] as const));
      return { walk: 0, block: false, move: MOVES[pick] ? pick : null };
    }

    const toward: -1 | 1 = foe.x > self.x ? 1 : -1;
    if (distance > this.profile.preferredRange + 8) {
      this.walkDir = toward;
      this.walkFor = this.rng.range(0.3, 0.7);
    } else if (distance < this.profile.preferredRange - 10) {
      this.walkDir = toward === 1 ? -1 : 1;
      this.walkFor = this.rng.range(0.15, 0.35);
    } else if (this.rng.chance(0.3)) {
      this.walkDir = this.rng.chance(0.5) ? toward : toward === 1 ? -1 : 1;
      this.walkFor = this.rng.range(0.1, 0.25);
    }
    return IDLE;
  }

  private meleeReach(): number {
    let reach = 0;
    for (const m of this.profile.moves)
      if (MOVES[m.move].reach > 0 && m.minRange === 0) reach = Math.max(reach, m.maxRange);
    return reach;
  }

  /** The fastest melee move in range and off cooldown. */
  private quickMove(distance: number, self: FighterState): MoveId | null {
    let best: MoveId | null = null;
    let bestStartup = Infinity;
    for (const m of this.profile.moves) {
      const def = MOVES[m.move];
      if (def.reach <= 0 || distance > m.maxRange || (self.cooldowns[m.move] ?? 0) > 0) continue;
      if (def.startup < bestStartup) {
        best = m.move;
        bestStartup = def.startup;
      }
    }
    return best;
  }
}
